import { type LinkedModuleBuilds, type SceneModule, type ScoringRunResult, type User } from '@webapp2/shared/model'
import { computed, ref } from 'vue'
import { localSettings } from '../main'
import { appRoutes, authRoutes, router } from '../router'
import {
  getCookie,
  registeredStateId,
  state,
  type BuildDownloadState,
  type ModuleBuildRegistrationState,
  type PermissionStatus,
  type VuplexData
} from '../state'
import { logOut } from './authentication-service'
import { currentEmbeddedModules } from './build-service'
import { cache } from './cache'
import { initialUpdate } from './entity-service'
import { linkedModuleBuildsByModuleBuildId, platformBuildData } from './linked-module-builds-service'
import { trackEvent } from './log-rocket-service'
import { qrLogin } from './qr-login'
import { handleRunEnd, handleRunResult, type RunLog } from './run-log-service'
import { isSurveyDue } from './survey-service'
import { emulateVuplex } from './vuplex-emulator'

export type UserData = {
  id: string
  email: string
  displayName?: string
  role: string
  country: string
  isOsso: boolean
}

export function toUserData({ id, email, displayName, role, country, isOsso }: User): UserData {
  return { id, email, displayName, role, country, isOsso }
}

export type BuildLaunchData = ModuleBuildLaunchData | RemoteModuleBuildLaunchData

export type LaunchMessage2Data = { playlist: (SingleLaunchData | MultiplayerLaunchData)[] }

export type MultiplayerLaunchData = {
  type: 'Multiplayer'
  address: string
  inviteCode: string
  multiplayerSessionId: string
  courseId?: string
}

export type SingleLaunchData = {
  type: 'SinglePlayer'
  build: BuildLaunchData
  courseId?: string
  isTestMode: boolean
  runTutorial: boolean
}

export type ModuleBuildLaunchData = {
  techniqueId: string
  techniquePath: string
}

export type RemoteModuleBuildLaunchData = ModuleBuildLaunchData & {
  id: string
  uri: string
  linkedModuleBuildsId: string
}

export type AuthenticatedData = {
  user: UserData
  organizationId: string
  userSettings: Record<string, unknown> | null
}

export type RegisterModulesData = {
  builds: LinkedModuleBuilds[]
}

export type RegisterModuleBuildData = {
  id: string
  techniqueId: string
  techniquePath: string
  uri: string
}

function toRegisterPayload(builds: LinkedModuleBuilds[]): RegisterModuleBuildData[] {
  return builds.map(platformBuildData).filter((x): x is RemoteModuleBuildLaunchData => x !== undefined)
}

export type LaunchMessage2 = {
  type: 'launch'
  data: LaunchMessage2Data
}

export type PermissionsData = { accepted: boolean; status?: PermissionStatus }

export type StartupLaunchData = {
  runId: string
  build: { id?: string; techniqueId: string; techniquePath: string; linkedModuleBuildsId?: string }
  courseId?: string
  isTestMode: boolean
  multiplayerSessionId?: string
}

export type StartupResponseData = {
  runLog?: RunLog
  launchData?: StartupLaunchData
  permissions: PermissionsData
  sceneModules?: SceneModule[]
  runResult?: ScoringRunResult
}

type ScanQrCodeMessage = { type: 'read-qr' }
type AuthenticatedMessage = {
  type: 'authenticated'
  data: AuthenticatedData
}
type RegisterModulesMessage = {
  type: 'register-modules'
  data: { builds: RegisterModuleBuildData[] }
}
type RequestPermissions = { type: 'request-permissions' }
type StartupMessage = { type: 'startup' }
type ExitApplicationMessage = { type: 'exit-application' }

type MessageToVuplex =
  | ScanQrCodeMessage
  | LaunchMessage2
  | AuthenticatedMessage
  | RegisterModulesMessage
  | RequestPermissions
  | StartupMessage
  | ExitApplicationMessage

type VuplexBuildDownloadStatus = {
  id: string
  downloaded: boolean
  downloadedBytes: number
  totalBytes: number
  error: boolean
}

type BuildDownloadStatusMessage = { type: 'build-download-status'; data: VuplexBuildDownloadStatus }
type QrCodeReadMessage = { type: 'qr-code'; data: string }
type QrModeCancelledMessage = { type: 'qr-mode-cancelled'; data: undefined }
type LogoutMessage = { type: 'logout'; data: undefined }
type PermissionsMessage = { type: 'permissions'; data: PermissionsData }
type StartupResponseMessage = { type: 'startup-response'; data: StartupResponseData }
type MessageFromVuplex =
  | QrCodeReadMessage
  | BuildDownloadStatusMessage
  | LogoutMessage
  | QrModeCancelledMessage
  | StartupResponseMessage
  | PermissionsMessage

let startupResponseData: StartupResponseData | undefined
let resolveStartUpRequest: (value?: StartupResponseData) => void
export const startupSceneModules = ref<SceneModule[]>([])

export async function initQuestBridge(): Promise<StartupResponseData | undefined> {
  parseVuplexInit()

  if (state.vuplex) {
    addMessageHandler()
    const startupData = new Promise<StartupResponseData | undefined>(resolve => (resolveStartUpRequest = resolve))
    // Wait for the startup response or timeout after 5 seconds (for older builds that don't send a startup response)
    const fiveSecondsInMs = 5000
    const timeoutInMs = window.testConfig?.startupResponseTimeoutInMilliseconds ?? fiveSecondsInMs
    postMessage({ type: 'startup' })
    const result = await Promise.race([
      startupData,
      new Promise<undefined>(x => setTimeout(() => x(undefined), timeoutInMs))
    ])
    return result
  }
  return undefined
}

function parseVuplexInit() {
  state.vuplex = localSettings?.vuplex ?? parseInitCookie()
  state.platform = state.vuplex ? (state.vuplex?.platform ?? 'quest') : 'web'

  if (localSettings?.vuplex || window.testConfig?.emulateVuplex) {
    emulateVuplex()
  }
  console.log(`Vuplex ${state.vuplex ? '' : 'not '}initialized`, state.vuplex)
}

function parseInitCookie() {
  const vuplexCookie = getCookie('vuplex_init')
  return vuplexCookie ? (JSON.parse(vuplexCookie) as VuplexData) : undefined
}

let scanQrTimeout: ReturnType<typeof setTimeout> | undefined

export function scanQrCode() {
  if (!state.permissionsAccepted) {
    void router.push(authRoutes.permissionsDenied)
    return
  }
  postMessage({ type: 'read-qr' })
  scanQrTimeout = setTimeout(() => void router.push(authRoutes.verifyQrCode), 200)
}

export function authenticated({
  user: { id, email, displayName, role, country, isOsso },
  organizationId,
  userSettings
}: AuthenticatedData) {
  postMessage({
    type: 'authenticated',
    data: { user: { id, email, displayName, role, country, isOsso }, organizationId, userSettings }
  })
}

export function launch2(data: LaunchMessage2Data) {
  trackEvent(
    'launch',
    Object.fromEntries(data.playlist.flatMap((x, i) => Object.entries(x).map(([key, value]) => [`${key}_${i}`, value])))
  )
  postMessage({ type: 'launch', data })
}

export function registerModules(data: RegisterModulesData) {
  postMessage({ type: 'register-modules', data: { builds: toRegisterPayload(data.builds) } })
}

export function requestPermissions() {
  postMessage({ type: 'request-permissions' })
}

export function exitApplication() {
  postMessage({ type: 'exit-application' })
}

function postMessage(message: MessageToVuplex) {
  if (!window.vuplex) {
    return
  }
  console.log('postMessage', message)
  window.vuplex.postMessage(message)
}

export function isEmbeddedOrSceneModule(techniquePath: string) {
  return (
    currentEmbeddedModules.value[techniquePath] !== undefined ||
    startupSceneModules.value.some(x => x.techniquePath === techniquePath)
  )
}

async function handleBuildDownloadStatus(message: VuplexBuildDownloadStatus) {
  if (message.error) {
    console.log(`Error while downloading module build: ${message.id}`)
  }
  const link = linkedModuleBuildsByModuleBuildId.value[message.id]
  if (!link) {
    if (message.error) {
      return
    }
    throw new Error(`No build found for download status, this should not happen. Build ID: ${message.id}`)
  }
  if (isEmbeddedOrSceneModule(link.techniquePath)) {
    return
  }
  const buildDownloadState: BuildDownloadState = message.error
    ? 'Error'
    : message.downloaded
      ? 'Downloaded'
      : message.downloadedBytes > 0
        ? 'Downloading'
        : 'Queued'
  const id = registeredStateId(link.techniquePath)
  const registeredState: ModuleBuildRegistrationState = {
    id,
    type: 'RegisteredState',
    techniquePath: link.techniquePath,
    linkedModuleBuildsId: link.id,
    downloadStatus: {
      id: message.id,
      downloadedBytes: message.downloadedBytes,
      totalBytes: message.totalBytes,
      state: buildDownloadState
    }
  }
  if (buildDownloadState === 'Downloaded') {
    await cache.set(id, registeredState)
  }
  state.registeredStates[id] = registeredState
}

function handleStartupData(startupData?: StartupResponseData) {
  console.log('Startup response received', startupData)
  if (startupData?.permissions) {
    setPermissions(startupData.permissions)
  }
  startupSceneModules.value = startupData?.sceneModules ?? []
  startupResponseData = startupData
  resolveStartUpRequest(startupData)
}

export async function getRunEndRoute(path: string) {
  const runId = startupResponseData?.runLog?.id ?? startupResponseData?.launchData?.runId
  if (!startupResponseData || !runId) {
    return
  }
  await initialUpdate
  if (startupResponseData.runResult && startupResponseData.launchData) {
    await handleRunResult(
      startupResponseData.runResult,
      runId,
      startupResponseData.launchData,
      startupSceneModules.value
    )
  } else if (startupResponseData.runLog) {
    await handleRunEnd(startupResponseData.runLog)
  }
  const run = runId ? state.runResults[runId] : undefined
  if (!run) {
    return
  }
  if (path.includes('/debrief/')) {
    return
  }
  const leftLegacyCollabFromUserPanel =
    Boolean(run.multiplayerSessionId) && !startupResponseData.runResult && !run.exitModule
  if (leftLegacyCollabFromUserPanel) {
    const route = run.courseId
      ? {
          name: 'join',
          params: {
            organizationId: state.organizationId,
            id: run?.multiplayerSessionId
          }
        }
      : { name: 'admin-multiplayer' }
    return route
  }
  const isRoot = path === '/' || path === ''
  if (isRoot && isSurveyDue(run)) {
    return {
      name: 'debrief-survey',
      params: {
        organizationId: state.organizationId,
        runId
      }
    }
  }
  return {
    name: 'debrief-overview',
    params: {
      organizationId: state.organizationId,
      runId
    }
  }
}

async function handlePermissions(data: PermissionsData) {
  setPermissions(data)
  const currentRouteName = router.currentRoute.value.name
  if (state.permissionsAccepted && currentRouteName === authRoutes.permissionsDenied.name) {
    await router.push(appRoutes.root)
  }
  if (!state.permissionsAccepted && shouldRouteToPermissionsDenied(currentRouteName)) {
    await router.push(authRoutes.permissionsDenied)
  }
}

function setPermissions(permissions: PermissionsData) {
  state.permissionsAccepted = permissions.accepted
  state.permissionStatus = permissions.status ?? (permissions.accepted ? 'granted' : 'settingsOpened')
}

export function shouldRouteToPermissionsDenied(routeName: unknown) {
  return (
    routeName !== authRoutes.permissionsDenied.name &&
    (routeName === authRoutes.login.name ||
      routeName === authRoutes.verifyQrCode.name ||
      routeName === appRoutes.qr.name)
  )
}

function addMessageHandler() {
  window.addEventListener('vuplexmessage', event => {
    const messageEvent = event as MessageEvent
    const message = parseMessage(messageEvent.data as string)
    console.log('Message from Vuplex', message)

    switch (message.type) {
      case 'qr-code':
        clearTimeout(scanQrTimeout)
        void qrLogin(message.data)
        break
      case 'qr-mode-cancelled':
        clearTimeout(scanQrTimeout)
        void router.push(authRoutes.login)
        break
      case 'build-download-status':
        void handleBuildDownloadStatus(message.data)
        break
      case 'startup-response':
        handleStartupData(message.data)
        break
      case 'logout':
        void logOut()
        break
      case 'permissions':
        void handlePermissions(message.data)
        break
      default:
        console.error('Invalid vuplex message', message)
    }
  })
}

function parseMessage(message: string) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const parsedMessage = JSON.parse(message)
  if (isVuplexMessage(parsedMessage)) {
    console.log('Vuplex message received', parsedMessage)
    return parsedMessage
  }
  throw new Error(`Invalid vuplex message ${message}`)
}

function isVuplexMessage(obj: unknown): obj is MessageFromVuplex {
  return typeof obj === 'object' && obj !== null && 'type' in obj
}

export const isQuest = computed<boolean>(() => state.vuplex !== undefined)
